import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, DollarSign, FileText, Tag, User, CreditCard, Building, AlertCircle } from 'lucide-react';
import TransactionsService from '@/api/transactions.service';
import ChartOfAccountsService from '@/api/chartofaccounts.service';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Loader2 } from 'lucide-react';
import { AccountTransaction, ChartOfAccount, TransactionType } from '@/types/index';
import { format, parseISO } from 'date-fns';

interface TransactionDetailProps {
  id?: string;
}

export default function TransactionDetail({ id: propsId }: TransactionDetailProps) {
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use id from props or from params
  const id = propsId || params.id;

  const [transaction, setTransaction] = useState<AccountTransaction | null>(null);
  const [account, setAccount] = useState<ChartOfAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchTransactionData = async () => {
      try {
        setIsLoading(true);
        const response = await TransactionsService.getTransactionById(id as string);
        if (response.data.result === 'success') {
          setTransaction(response.data.accountTransaction);
          
          // Fetch the account details
          if (response.data.accountTransaction.chartOfAccountId) {
            const accountResponse = await ChartOfAccountsService.getChartOfAccountById(
              response.data.accountTransaction.chartOfAccountId
            );
            if (accountResponse.data.result === 'success') {
              setAccount(accountResponse.data.chartOfAccount);
            }
          }
        } else {
          toast({
            title: 'Error',
            description: response.data.message,
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('Error fetching transaction:', error);
        toast({
          title: 'Error',
          description: 'Failed to load transaction details',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchTransactionData();
    }
  }, [id, toast]);
  
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (e) {
      return dateString;
    }
  };
  
  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount); // Assuming amount is stored in cents
  };
  
  const getTransactionTypeColor = (type: TransactionType) => {
    return type === 'DEBIT' 
      ? 'bg-amber-50 text-amber-700 border-amber-200' 
      : 'bg-blue-50 text-blue-700 border-blue-200';
  };
  
  const getReferenceLabel = (referenceType?: string) => {
    if (!referenceType) return 'Manual';
    
    const labels: Record<string, string> = {
      'invoice': 'Invoice',
      'receipt': 'Receipt',
      'bill': 'Bill',
      'quotation': 'Quotation',
      'manual': 'Manual Entry'
    };
    
    return labels[referenceType] || 'Reference';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <Card className="w-full max-w-lg">
            <CardContent className="flex flex-col items-center py-8">
              <AlertCircle className="h-12 w-12 text-slate-300 mb-4" />
              <h2 className="text-lg font-medium mb-2">Transaction Not Found</h2>
              <p className="text-slate-500 mb-6 text-center">
                The transaction you're looking for doesn't exist or you don't have permission to view it.
              </p>
              <Button
                onClick={() => navigate('/transactions')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Transactions
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getReferenceLink = () => {
    if (!transaction.referenceType || !transaction.referenceId) return '#';
    
    switch (transaction.referenceType) {
      case 'invoice': return `/invoice/${transaction.referenceId}`;
      case 'receipt': return `/receipt/${transaction.referenceId}`;
      case 'bill': return `/bill/${transaction.referenceId}`;
      case 'quotation': return `/quotation/${transaction.referenceId}`;
      default: return '#';
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            size="sm"
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={16} />
            Back
          </Button>
          <h1 className="text-2xl font-semibold">Transaction Details</h1>
          {transaction.publicId && (
            <Badge variant="outline" className="ml-2 font-mono">
              {transaction.publicId}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Transaction Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Transaction Type</h3>
                <Badge variant="outline" className={getTransactionTypeColor(transaction.transactionType)}>
                  {transaction.transactionType}
                </Badge>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Amount</h3>
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-1 text-muted-foreground" />
                  <span className="text-xl font-bold">
                    {formatAmount(transaction.amount, transaction.currency)}
                  </span>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Transaction Date</h3>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>{formatDate(transaction.transactionDate)}</span>
                </div>
              </div>
              
              {transaction.description && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Description</h3>
                  <p className="text-sm">{transaction.description}</p>
                </div>
              )}
              
              <Separator />
              
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Created</h3>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span>{formatDate(transaction.dateCreated)}</span>
                </div>
              </div>

              {transaction.dateUpdated && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Last Updated</h3>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{formatDate(transaction.dateUpdated)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div className="md:col-span-2">
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Account Information</CardTitle>
              </CardHeader>
              <CardContent>
                {account ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Account</h3>
                        <p className="text-lg font-medium">{account.name}</p>
                        <div className="flex items-center mt-1">
                          <CreditCard className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                          <span className="text-sm font-mono text-muted-foreground">{account.code}</span>
                        </div>
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/chart-of-accounts/${account.publicId}`)}
                      >
                        View Account
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Account Type</h3>
                        <Badge variant="outline">
                          {account.type}
                        </Badge>
                        {account.subtype && (
                          <span className="ml-2 text-sm text-muted-foreground">{account.subtype}</span>
                        )}
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Status</h3>
                        <Badge className={account.isActive ? "bg-green-500" : "bg-blue-500"}>
                          {account.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-6 text-muted-foreground">
                    <p>Account information not available</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Reference Information</CardTitle>
              </CardHeader>
              <CardContent>
                {transaction.referenceType || transaction.reference ? (
                  <div className="space-y-4">
                    {transaction.referenceType && (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Reference Type</h3>
                        <Badge variant="outline" className="capitalize">
                          {getReferenceLabel(transaction.referenceType)}
                        </Badge>
                      </div>
                    )}
                    
                    {transaction.reference && (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">Reference</h3>
                        {transaction.referenceId ? (
                          <a 
                            href={getReferenceLink()} 
                            className="text-primary hover:underline flex items-center"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            {transaction.reference}
                          </a>
                        ) : (
                          <span>{transaction.reference}</span>
                        )}
                      </div>
                    )}
                    
                    <div className="bg-slate-50 rounded-md p-4">
                      <div className="text-sm">
                        <p className="font-medium mb-1">Transaction Effect</p>
                        <p>
                          This transaction {transaction.transactionType === 'DEBIT' ? 'increases' : 'decreases'} the {' '}
                          {account?.type.toLowerCase()} account "{account?.name}".
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-6 text-muted-foreground">
                    <p>No reference information available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
} 